import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;

// Create browser history for SPA tracking (only on client side)
let browserHistory: any = null;
if (typeof window !== 'undefined') {
  browserHistory = createBrowserHistory();
}

export const initializeAppInsights = () => {
  // Only initialize on client side and if not already initialized
  if (typeof window === 'undefined' || appInsights) {
    return { appInsights, reactPlugin };
  }

  // Get connection string from environment variable
  const connectionString = process.env.NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING;
  
  if (!connectionString) {
    console.warn('Application Insights connection string not found. Please set NEXT_PUBLIC_APPLICATIONINSIGHTS_CONNECTION_STRING environment variable.');
    return { appInsights: null, reactPlugin: null };
  }

  // Initialize React plugin
  reactPlugin = new ReactPlugin();

  // Initialize Application Insights
  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory || undefined }
      },
      // Enable automatic route change tracking
      enableAutoRouteTracking: true,
      // Disable CORS correlation to avoid conflicts with proxied requests
      enableCorsCorrelation: false,
      // Disable request headers tracking to avoid fetch monitoring issues
      enableRequestHeaderTracking: false,
      // Disable response headers tracking to avoid fetch monitoring issues
      enableResponseHeaderTracking: false,
      // Disable automatic AJAX dependency tracking to avoid webpack conflicts
      disableAjaxTracking: true,
      // Disable automatic fetch dependency tracking to avoid webpack conflicts
      disableFetchTracking: true,
      // Track unhandled exceptions
      enableUnhandledPromiseRejectionTracking: true,
      // Enable debug logging in development only
      loggingLevelTelemetry: process.env.NODE_ENV === 'development' ? 1 : 0,
      // Disable console logging in production
      loggingLevelConsole: process.env.NODE_ENV === 'development' ? 1 : 0,
      // Disable telemetry in development if desired
      disableTelemetry: process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DISABLE_TELEMETRY === 'true',
      // Disable automatic instrumentation that might conflict with Next.js
      instrumentationKey: undefined,
      // Additional stability settings
      samplingPercentage: 100,
      maxBatchSizeInBytes: 25000,
      maxBatchInterval: 15000,
    }
  });

  try {
    appInsights.loadAppInsights();

    // Set up custom error boundary tracking
    appInsights.addTelemetryInitializer((envelope) => {
      try {
        envelope.tags = envelope.tags || {};
        envelope.tags['ai.session.id'] = appInsights?.context?.session?.id || '';
        envelope.tags['ai.user.id'] = appInsights?.context?.user?.id || '';
        envelope.tags['ai.application.ver'] = '1.0.0';
        envelope.tags['ai.device.type'] = 'Browser';
      } catch (error) {
        // Silently handle telemetry initializer errors
        if (process.env.NODE_ENV === 'development') {
          console.warn('Application Insights telemetry initializer error:', error);
        }
      }
    });
  } catch (error) {
    console.warn('Failed to load Application Insights:', error);
    // Continue without Application Insights if loading fails
    appInsights = null;
    reactPlugin = null;
  }

  return { appInsights, reactPlugin };
};

// Custom tracking functions
export const trackOrderEvent = (eventName: string, properties?: { [key: string]: any }) => {
  // Only track on client side
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackEvent({
      name: `Order_${eventName}`,
      properties: {
        category: 'Order',
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  }
};

export const trackJWTEvent = (eventName: string, properties?: { [key: string]: any }) => {
  // Only track on client side
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackEvent({
      name: `JWT_${eventName}`,
      properties: {
        category: 'Authentication',
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  }
};

export const trackAPICall = (url: string, method: string, statusCode: number, duration: number, success: boolean, requestHeaders?: Record<string, string>, responseHeaders?: Record<string, string>) => {
  // Only track on client side
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    try {
      appInsights.trackDependencyData({
        id: `${method}_${url}_${Date.now()}`,
        name: `API_${method}_${url}`,
        data: url,
        duration,
        success,
        responseCode: statusCode,
        type: 'HTTP',
        properties: {
          method,
          url,
          category: 'API',
          requestHeaders: requestHeaders ? JSON.stringify(requestHeaders) : undefined,
          responseHeaders: responseHeaders ? JSON.stringify(responseHeaders) : undefined,
          timestamp: new Date().toISOString(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          referrer: typeof document !== 'undefined' ? document.referrer : 'unknown'
        }
      });
    } catch (error) {
      // Silently handle tracking errors to avoid disrupting application flow
      if (process.env.NODE_ENV === 'development') {
        console.warn('Application Insights API tracking error:', error);
      }
    }
  }
};

export const trackError = (error: Error, properties?: { [key: string]: any }) => {
  // Only track on client side
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackException({
      exception: error,
      properties: {
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  }
};

export const trackPageView = (name?: string, url?: string, properties?: { [key: string]: any }) => {
  // Only track on client side
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackPageView({
      name,
      uri: url,
      properties
    });
  }
};

// Performance monitoring functions
export const trackPerformanceMetric = (name: string, value: number, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackMetric({
      name,
      average: value,
      properties: {
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  }
};

export const trackUserFlow = (flowName: string, step: string, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackEvent({
      name: `UserFlow_${flowName}_${step}`,
      properties: {
        flow: flowName,
        step,
        timestamp: new Date().toISOString(),
        sessionId: appInsights.context?.session?.id,
        userId: appInsights.context?.user?.id,
        ...properties
      }
    });
  }
};

export const trackBusinessMetric = (metricName: string, value: number, currency?: string, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackEvent({
      name: `Business_${metricName}`,
      properties: {
        value,
        currency,
        timestamp: new Date().toISOString(),
        ...properties
      }
    });
  }
};

// Advanced error tracking with context
export const trackDetailedError = (error: Error, context: string, userAction?: string, properties?: { [key: string]: any }) => {
  if (typeof window === 'undefined') return;
  
  if (appInsights) {
    appInsights.trackException({
      exception: error,
      properties: {
        context,
        userAction,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        stack: error.stack,
        ...properties
      }
    });
  }
};

// Export the instances for use in React components
export { appInsights, reactPlugin, browserHistory };